import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import {
  Box,
  Checkbox,
  ClickAwayListener,
  InputBase,
  Paper,
  Popper,
  Typography
} from '@mui/material'
import { UI_TEXT_CLEAR_SEARCH, UI_TEXT_SEARCH_PLACEHOLDER } from '../../constants/uiText'
import { UI_COLOR_TEXT_READONLY } from '../../constants/uiColors'
import GoogleIcon from '../../icon/GoogleIcon'

const DEFAULT_FILTER_VALUE = {
  mode: 'all',
  values: []
}

const INITIAL_OPTIONS_BATCH = 25
const OPTIONS_BATCH_SIZE = 25
const LOAD_MORE_SCROLL_THRESHOLD = 48
const OPTION_ROW_HEIGHT = 38
const MIN_ASYNC_SEARCH_CHARS = 3
const DEFAULT_ASYNC_SEARCH_DEBOUNCE_MS = 800
const LOCKED_CHECKBOX_STYLE = {
  '--mui-filter-select-locked-checkbox-color': UI_COLOR_TEXT_READONLY
}

const normalizeText = (value) => {
  return String(value || '')
    .trim()
    .toLowerCase()
}

const MuiFilterSelect = ({
  title,
  options = [],
  pinnedOptions = [],
  value = DEFAULT_FILTER_VALUE,
  onChange,
  disabled = false,
  searchable = true,
  autoFocusSearch = true,
  singleSelect = false,
  width = 290,
  dropdownWidth = 390,
  searchPlaceholder = UI_TEXT_SEARCH_PLACEHOLDER,
  matchTriggerWidth = false,
  inlineDropdown = false,
  // Optional async/server mode — unused by existing callers; keeps current UX intact.
  onSearchChange,
  onLoadMore,
  hasMore = false,
  loading = false,
  selectAllAsNoFilter = false,
  disableDropdownFlip = false,
  maxVisibleOptions,
  searchOnInput = false,
  searchDebounceMs = DEFAULT_ASYNC_SEARCH_DEBOUNCE_MS,
  minAsyncSearchChars = MIN_ASYNC_SEARCH_CHARS,
  dropdownPaperClassName = '',
  dropdownPopperClassName = '',
  searchIconNode = null,
  clearIconNode = null,
  openIconNode = null,
  closedIconNode = null
}) => {
  const anchorRef = useRef(null)
  const searchInputRef = useRef(null)
  const onSearchChangeRef = useRef(onSearchChange)
  const lastSubmittedSearchRef = useRef('')
  const asyncSearch = typeof onSearchChange === 'function'
  const asyncLoadMore = typeof onLoadMore === 'function'
  const initialBatchSize = maxVisibleOptions || INITIAL_OPTIONS_BATCH
  const batchSize = maxVisibleOptions || OPTIONS_BATCH_SIZE

  const [open, setOpen] = useState(false)
  const [search, setSearch] = useState('')
  const [visibleOptionsCount, setVisibleOptionsCount] = useState(initialBatchSize)

  const selectedMode = value?.mode || 'all'
  const selectedValues = value?.values || []
  const selectedValuesSet = useMemo(() => new Set(selectedValues), [selectedValues])
  const valueRef = useRef(value)
  const syncedValueRef = useRef(value)

  useLayoutEffect(() => {
    if (syncedValueRef.current !== value) {
      syncedValueRef.current = value
      valueRef.current = value
    }
  }, [value])

  const commitValue = (nextValue) => {
    valueRef.current = nextValue
    onChange(nextValue)
  }
  const pinnedValuesSet = useMemo(
    () => new Set(pinnedOptions.map((option) => option.value)),
    [pinnedOptions]
  )
  const selectedPinnedValues = useMemo(
    () => selectedValues.filter((optionValue) => pinnedValuesSet.has(optionValue)),
    [pinnedValuesSet, selectedValues]
  )
  const optionValuesSet = useMemo(() => new Set(options.map((option) => option.value)), [options])
  // Pinned already listed in options are rendered once as locked options.
  const pinnedOnlyOptions = useMemo(
    () => pinnedOptions.filter((option) => !optionValuesSet.has(option.value)),
    [optionValuesSet, pinnedOptions]
  )
  const selectableOptions = useMemo(
    () => options.filter((option) => !pinnedValuesSet.has(option.value)),
    [options, pinnedValuesSet]
  )

  const isEverythingSelected = selectedMode === 'everything'
  const selectedCount = isEverythingSelected
    ? selectableOptions.length
    : selectedMode === 'all'
      ? 0
      : selectedValues.length
  const hasOptions = options.length > 0
  const hasSelectableOptions = selectableOptions.length > 0
  const areAllSelected = useMemo(() => {
    if (singleSelect) return false
    if (!hasSelectableOptions || selectedMode === 'all') return false
    if (selectedMode === 'everything') return true

    return selectableOptions.every((option) => selectedValuesSet.has(option.value))
  }, [hasSelectableOptions, selectableOptions, selectedMode, selectedValuesSet, singleSelect])
  const isOptionChecked = (optionValue) =>
    (isEverythingSelected && !pinnedValuesSet.has(optionValue)) ||
    selectedValuesSet.has(optionValue)
  const isRaised = open

  const searchableOptions = useMemo(
    () =>
      options.map((option) => ({
        ...option,
        normalizedLabel: normalizeText(option.label)
      })),
    [options]
  )

  const filteredOptions = useMemo(() => {
    if (asyncSearch) return searchableOptions

    const normalizedSearch = normalizeText(search)

    if (!normalizedSearch) return searchableOptions

    return searchableOptions.filter((option) => option.normalizedLabel.includes(normalizedSearch))
  }, [asyncSearch, search, searchableOptions])

  const visibleOptions = useMemo(() => {
    if (asyncLoadMore) return filteredOptions
    return filteredOptions.slice(0, visibleOptionsCount)
  }, [asyncLoadMore, filteredOptions, visibleOptionsCount])

  const hasMoreOptions = asyncLoadMore ? hasMore : visibleOptionsCount < filteredOptions.length

  const optionsMaxHeight = maxVisibleOptions ? maxVisibleOptions * OPTION_ROW_HEIGHT : undefined

  useEffect(() => {
    onSearchChangeRef.current = onSearchChange
  }, [onSearchChange])

  const submitSearchValue = (rawSearch) => {
    if (!asyncSearch || disabled) return

    const normalizedSearch = String(rawSearch || '').trim()
    if (normalizedSearch !== '' && normalizedSearch.length < minAsyncSearchChars) return
    if (lastSubmittedSearchRef.current === normalizedSearch) return

    lastSubmittedSearchRef.current = normalizedSearch
    onSearchChangeRef.current?.(normalizedSearch)
  }

  useEffect(() => {
    if (open && searchable && autoFocusSearch) {
      const timer = setTimeout(() => {
        searchInputRef.current?.focus()
      }, 0)

      return () => clearTimeout(timer)
    }
  }, [autoFocusSearch, open, searchable])

  useEffect(() => {
    if (!open) {
      setVisibleOptionsCount(initialBatchSize)
      return
    }

    setVisibleOptionsCount(initialBatchSize)
  }, [search, initialBatchSize, open, options.length])

  useEffect(() => {
    if (!open || !asyncSearch || !searchOnInput) return undefined

    const timer = setTimeout(() => {
      submitSearchValue(search)
    }, searchDebounceMs)

    return () => clearTimeout(timer)
  }, [asyncSearch, open, search, searchDebounceMs, searchOnInput])

  const handleToggleOpen = () => {
    if (disabled) return
    setOpen((prev) => !prev)
  }

  const handleClose = () => {
    setOpen(false)
    setSearch('')
    setVisibleOptionsCount(initialBatchSize)
    if (asyncSearch && search) {
      submitSearchValue('')
    }
  }

  const handleOptionsScroll = (event) => {
    if (!hasMoreOptions || loading) return

    const { scrollTop, clientHeight, scrollHeight } = event.currentTarget
    const remainingScroll = scrollHeight - (scrollTop + clientHeight)

    if (remainingScroll > LOAD_MORE_SCROLL_THRESHOLD) {
      return
    }

    if (asyncLoadMore) {
      onLoadMore()
      return
    }

    setVisibleOptionsCount((prevCount) => Math.min(prevCount + batchSize, filteredOptions.length))
  }

  const handleSearchInputChange = (event) => {
    setSearch(event.target.value)
  }

  const handleClearSearch = (event) => {
    event.preventDefault()
    event.stopPropagation()

    if (!search) return

    setSearch('')
    setVisibleOptionsCount(initialBatchSize)

    if (asyncSearch) {
      submitSearchValue('')
    }

    searchInputRef.current?.focus()
  }

  const submitAsyncSearch = () => {
    if (!asyncSearch || disabled) return

    submitSearchValue(search)
  }

  const handleSearchKeyDown = (event) => {
    if (!asyncSearch || event.key !== 'Enter') return
    event.preventDefault()
    submitAsyncSearch()
  }

  const handleSelectAll = () => {
    if (!hasSelectableOptions) return

    const currentValue = valueRef.current || DEFAULT_FILTER_VALUE
    const currentMode = currentValue.mode || 'all'
    const currentValues = currentValue.values || []
    const currentValuesSet = new Set(currentValues)
    const currentPinnedValues = currentValues.filter((optionValue) =>
      pinnedValuesSet.has(optionValue)
    )
    const allSelected =
      currentMode === 'everything' ||
      (currentMode !== 'all' &&
        selectableOptions.every((option) => currentValuesSet.has(option.value)))

    if (allSelected) {
      if (!currentPinnedValues.length) {
        commitValue({
          mode: 'all',
          values: []
        })
        return
      }

      commitValue({
        mode: 'custom',
        values: currentPinnedValues
      })
      return
    }

    if (selectAllAsNoFilter && !currentPinnedValues.length) {
      commitValue({
        mode: 'everything',
        values: []
      })
      return
    }

    commitValue({
      mode: 'custom',
      values: Array.from(
        new Set([...currentPinnedValues, ...selectableOptions.map((option) => option.value)])
      )
    })
  }

  const handleToggleOption = (optionValue) => {
    const currentValue = valueRef.current || DEFAULT_FILTER_VALUE
    const currentMode = currentValue.mode || 'all'
    const currentValues = currentValue.values || []
    const currentValuesSet = new Set(currentValues)
    const currentPinnedValues = currentValues.filter((item) => pinnedValuesSet.has(item))

    if (singleSelect) {
      if (currentValuesSet.has(optionValue)) {
        commitValue({
          mode: 'all',
          values: []
        })
      } else {
        commitValue({
          mode: 'custom',
          values: [optionValue]
        })
      }

      setOpen(false)
      setSearch('')
      return
    }

    if (currentMode === 'everything') {
      if (pinnedValuesSet.has(optionValue)) return

      const nextValues = selectableOptions
        .map((option) => option.value)
        .filter((item) => item !== optionValue)

      if (!nextValues.length) {
        commitValue({
          mode: 'all',
          values: []
        })
        return
      }

      commitValue({
        mode: 'custom',
        values: nextValues
      })
      return
    }

    if (currentMode === 'all') {
      commitValue({
        mode: 'custom',
        values: [optionValue]
      })
      return
    }

    const exists = currentValuesSet.has(optionValue)

    if (exists) {
      if (pinnedValuesSet.has(optionValue)) {
        return
      }

      const nextValues = currentValues.filter((item) => item !== optionValue)

      if (!nextValues.length) {
        if (currentPinnedValues.length) {
          commitValue({
            mode: 'custom',
            values: currentPinnedValues
          })
          return
        }

        commitValue({
          mode: 'all',
          values: []
        })
        return
      }

      commitValue({
        mode: 'custom',
        values: nextValues
      })
      return
    }

    commitValue({
      mode: 'custom',
      values: [...currentValues, optionValue]
    })
  }

  const handleOptionMouseDown = (event) => {
    event.preventDefault()
  }

  const resolvedDropdownWidth =
    matchTriggerWidth && anchorRef.current
      ? anchorRef.current.getBoundingClientRect().width
      : dropdownWidth

  const dropdownContent = (
    <Paper
      elevation={inlineDropdown ? 0 : 6}
      className={`mui-filter-select__paper ${inlineDropdown ? 'mui-filter-select__paper--inline' : ''} ${dropdownPaperClassName}`}
      style={{ width: inlineDropdown ? '100%' : resolvedDropdownWidth }}
    >
      {searchable && (
        <Box className="mui-filter-select__search-wrap">
          <InputBase
            className="mui-filter-select__search-input"
            inputRef={searchInputRef}
            value={search}
            onChange={handleSearchInputChange}
            onKeyDown={handleSearchKeyDown}
            startAdornment={
              <Box className="mui-filter-select__search-icon" aria-hidden="true">
                {searchIconNode || <GoogleIcon name="search" size={20} />}
              </Box>
            }
            inputProps={{
              enterKeyHint: asyncSearch ? 'search' : undefined
            }}
            placeholder={searchPlaceholder}
            endAdornment={
              search ? (
                <Box
                  component="button"
                  type="button"
                  aria-label={UI_TEXT_CLEAR_SEARCH}
                  className="mui-filter-select__search-clear"
                  onMouseDown={(event) => event.preventDefault()}
                  onClick={handleClearSearch}
                >
                  {clearIconNode || <GoogleIcon name="close" size={18} />}
                </Box>
              ) : null
            }
            fullWidth
          />
        </Box>
      )}

      {hasSelectableOptions && !singleSelect && !asyncSearch && (
        <Box
          onMouseDown={handleOptionMouseDown}
          onClick={handleSelectAll}
          className="mui-filter-select__select-all"
        >
          <Checkbox
            checked={areAllSelected}
            disableRipple
            className="mui-filter-select__checkbox"
          />

          <Typography className="mui-filter-select__select-all-text">Seleccionar todos</Typography>
        </Box>
      )}

      {pinnedOnlyOptions.map((option, index) => {
        const checked = isOptionChecked(option.value)

        return (
          <Box
            key={`${option.value}-${index}`}
            className="mui-filter-select__select-all mui-filter-select__select-all--pinned"
            aria-disabled="true"
          >
            <Checkbox
              checked={checked}
              disableRipple
              tabIndex={-1}
              slotProps={{ input: { readOnly: true, tabIndex: -1 } }}
              style={LOCKED_CHECKBOX_STYLE}
              className="mui-filter-select__checkbox mui-filter-select__checkbox--locked"
            />

            <Typography className="mui-filter-select__select-all-text">{option.label}</Typography>
          </Box>
        )
      })}

      <Box
        className="mui-filter-select__options"
        onScroll={handleOptionsScroll}
        style={optionsMaxHeight ? { maxHeight: optionsMaxHeight } : undefined}
      >
        {visibleOptions.map((option, index) => {
          const checked = isOptionChecked(option.value)
          const isPinned = pinnedValuesSet.has(option.value)
          const optionClassName = isPinned
            ? 'mui-filter-select__option mui-filter-select__option--pinned'
            : 'mui-filter-select__option'
          const checkboxClassName = isPinned
            ? 'mui-filter-select__checkbox mui-filter-select__checkbox--option mui-filter-select__checkbox--locked'
            : 'mui-filter-select__checkbox mui-filter-select__checkbox--option'

          return (
            <Box
              key={`${option.value}-${index}`}
              onMouseDown={isPinned ? undefined : handleOptionMouseDown}
              onClick={isPinned ? undefined : () => handleToggleOption(option.value)}
              className={optionClassName}
              aria-disabled={isPinned ? 'true' : undefined}
            >
              <Checkbox
                checked={checked}
                disableRipple
                tabIndex={isPinned ? -1 : undefined}
                slotProps={isPinned ? { input: { readOnly: true, tabIndex: -1 } } : undefined}
                style={isPinned ? LOCKED_CHECKBOX_STYLE : undefined}
                className={checkboxClassName}
              />

              <Typography className="mui-filter-select__option-label">{option.label}</Typography>
            </Box>
          )
        })}

        {!filteredOptions.length && !loading && !pinnedOptions.length && (
          <Typography className="mui-filter-select__empty">No hay resultados</Typography>
        )}

        {loading && <Typography className="mui-filter-select__empty">Cargando...</Typography>}
      </Box>
    </Paper>
  )

  return (
    <ClickAwayListener onClickAway={handleClose}>
      <Box className="mui-filter-select" style={{ width }}>
        <Box
          ref={anchorRef}
          onClick={handleToggleOpen}
          className={`mui-filter-select__trigger ${open ? 'mui-filter-select__trigger--open' : ''} ${disabled ? 'mui-filter-select__trigger--disabled' : ''}`}
        >
          {isRaised ? (
            <Box className="mui-filter-select__raised-label">
              <Typography className="mui-filter-select__raised-title">{title}</Typography>

              {selectedCount > 0 && (
                <Typography className="mui-filter-select__raised-count">
                  ({selectedCount})
                </Typography>
              )}
            </Box>
          ) : null}

          <Box className="mui-filter-select__content">
            <Box
              className={`mui-filter-select__label-wrap ${open ? 'mui-filter-select__label-wrap--hidden' : ''}`}
            >
              <Typography className="mui-filter-select__title">{title}</Typography>

              {selectedCount > 0 && (
                <Typography className="mui-filter-select__count">({selectedCount})</Typography>
              )}
            </Box>

            <Box
              className={`mui-filter-select__icon ${open ? 'mui-filter-select__icon--open' : ''}`}
            >
              {open
                ? openIconNode || <GoogleIcon name="arrow_drop_up" size={24} />
                : closedIconNode || <GoogleIcon name="arrow_drop_down" size={24} />}
            </Box>
          </Box>
        </Box>

        {inlineDropdown ? (
          open ? (
            <Box className="mui-filter-select__inline-layer">{dropdownContent}</Box>
          ) : null
        ) : (
          <Popper
            open={open}
            anchorEl={anchorRef.current}
            placement="bottom-start"
            modifiers={disableDropdownFlip ? [{ name: 'flip', enabled: false }] : undefined}
            popperOptions={disableDropdownFlip ? { strategy: 'fixed' } : undefined}
            className={`mui-filter-select__popper ${dropdownPopperClassName}`}
          >
            {dropdownContent}
          </Popper>
        )}
      </Box>
    </ClickAwayListener>
  )
}

export default MuiFilterSelect
export { DEFAULT_FILTER_VALUE }
